import { styled } from "@mui/material/styles";

export const StyledInput = styled("input")(({ theme }) => ({
  border: "#fff 2px solid",
  borderRadius: "8px",
  fontFamily: "inherit",
  padding: "15px",
  color: "#717680",
  fontSize: "14px",
  transition: "all 0.2s ease-in",
  appearance: "none",

  '&[type="number"]': {
    width: "100px",
    textAlign: "right",
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "&[type=number]": {
      MozAppearance: "textfield", // Firefox
    },
  },

  // 🎯 Checkbox input
  '&[type="checkbox"]': {
    margin: 0,
    width: "22px",
    height: "22px",
    padding: "5px",
    border: "0.1em solid #fff",
    borderRadius: "6px",
    transform: "translateY(-0.075em)",
    placeContent: "center",
    cursor: "pointer",
    "&:before": {
      content: '""',
      width: "10px",
      height: "10px",
      clipPath: "polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%)",
      transform: "scale(0)",
      transformOrigin: "bottom left",
      transition: "120ms transform ease-in-out",
      boxShadow: `inset 1em 1em #030039`,
      backgroundColor: "red",
      display: "block",
    },
    "&:checked": {
      background: `${theme.palette.background.button_primary}`,
      borderColor: `${theme.palette.background.button_primary}`,
      "&:before": {
        transform: "scale(1)",
      },
    },
    "& + span": {
      marginRight: "5px",
      "& + a": {
        color: "#D1C7FF",
        textDecoration: "none",
      },
    },
  },

  "&[type='radio']": {
    WebkitAppearance: "none", // Removed leading space
    appearance: "none", // Changed from "" to "none"
    backgroundColor: "#fff",
    margin: 0,
    font: "inherit",
    width: "20px",
    height: "20px",
    border: `1px solid #211F45`,
    borderRadius: "50%",
    transform: "translateY(-0.075em)",
    padding: "8px",
    // Added Grid to effortlessly center the inner dot
    display: "grid",
    placeContent: "center",

    "&::before": {
      content: '""',
      width: "0.8em", // Made smaller than the container so it looks like a dot
      height: "0.8em",
      borderRadius: "50%",
      transform: "scale(0)",
      transition: "120ms transform ease-in-out",
      boxShadow: `inset 1em 1em #211F45`, // backgroundColor: "red" also works here
      marginLeft: "0.5px",
    },
    "&:checked": {
      borderColor: theme.palette.background.button_primary,
    },
    // Combined the selectors instead of nesting them
    "&:checked::before": {
      transform: "scale(1)",
    },
  },

  // 🎯 Text input (default)
  '&[type="text"], &[type="password"], &[type="email"]': {
    width: "100%",
    "&:focus": {
      outline: "none",
      border: `${theme.palette.background.button_primary} 2px solid`,
    },
    "&.invalid-input": {
      border: `${theme.palette.text.warning_primary} 2px solid`,
    },
    "& + .password-toggler": {
      position: "absolute",
      border: 0,
      background: "transparent",
      color: "#000",
      right: "15px",
      top: "29%",
      width: "auto",
      textAlign: "right",
      padding: 0,
      cursor: "pointer",
      "& svg": {
        width: "25px",
        height: "25px",
      },
    },
  },
  // 🎯 Email input
  '&[type="email"]': {
    width: "100%",
  },
}));

// ── Select / Multiselect ──────────────────────────────────────────────────────

export const SelectWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  minWidth: 200,
  [theme.breakpoints.down("sm")]: {
    minWidth: "0",
  },
}));

export const SelectTrigger = styled("div")(
  ({ $open, $disabled, $error, theme }) => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "0 14px",
    height: 50,
    borderRadius: 10,
    background: $open ? "rgba(150,46,255,0.08)" : "rgba(3,0,57,0.9)",
    border: `1px solid ${
      $error
        ? theme.palette.text.warning_primary
        : $open
          ? "rgba(150,46,255,0.6)"
          : "rgba(255,255,255,0.10)"
    }`,
    boxShadow: $open ? "0 0 0 3px rgba(150,46,255,0.14)" : "none",
    color: "#FEFEFE",
    fontSize: 14,
    cursor: $disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit",
    transition: "border-color 150ms, background 150ms, box-shadow 150ms",
    opacity: $disabled ? 0.6 : 1,
    boxSizing: "border-box",
    "&:hover": {
      borderColor: $error
        ? theme.palette.text.warning_primary
        : $open
          ? "rgba(150,46,255,0.6)"
          : "rgba(255,255,255,0.28)",
    },
  }),
);

export const SelectValueSpan = styled("span")(({ $isPlaceholder }) => ({
  flex: 1,
  textAlign: "left",
  color: $isPlaceholder ? "#A87EFF" : "#D1C7FF",
  fontSize: 14,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
}));

export const SelectChevron = styled("span")(({ $open }) => ({
  opacity: 0.6,
  display: "inline-flex",
  alignItems: "center",
  transform: $open ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform 150ms",
  color: "#FEFEFE",
}));

export const SelectMenu = styled("div")({
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  right: 0,
  background: "#0A0754",
  border: "1px solid rgba(150,46,255,0.25)",
  borderRadius: 10,
  padding: 6,
  zIndex: 999,
  boxShadow: "0 20px 40px rgba(0,0,0,0.5),0 0 0 1px rgba(150,46,255,0.08)",
  maxHeight: 200,
  overflowY: "auto",
});

export const SelectOption = styled("div")(({ $isSelected, $isHovered }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  background: $isSelected
    ? "rgba(150,46,255,0.18)"
    : $isHovered
      ? "rgba(255,255,255,0.05)"
      : "transparent",
  color: $isSelected || $isHovered ? "#FEFEFE" : "#D1C7FF",
  fontSize: 14,
  cursor: "pointer",
  borderRadius: 6,
  fontFamily: "inherit",
  transition: "background 80ms",
}));

// ── Dark theme text input ─────────────────────────────────────────────────────

export const DarkInputBox = styled("div")(({ theme, $focused, $error }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  padding: "0 14px",
  height: 50,
  gap: "8px",
  borderRadius: 10,
  background: theme.palette.background.primary,
  border: `1px solid ${
    $error
      ? theme.palette.text.warning_primary
      : $focused
        ? "rgba(150,46,255,0.6)"
        : "rgba(255,255,255,0.10)"
  }`,
  boxShadow: $focused ? "0 0 0 3px rgba(150,46,255,0.14)" : "none",
  transition: "border-color 150ms, box-shadow 150ms",
  boxSizing: "border-box",
}));

export const DarkTextInput = styled("input")(({ theme }) => ({
  flex: 1,
  background: "none",
  border: "none",
  fontSize: 14,
  outline: "none",
  fontFamily: "inherit",
  color: theme.palette.text.primary,
  padding: 0,
  "&::placeholder": {
    color: "rgba(255,255,255,0.35)",
  },
}));

export const DarkTextareaBox = styled("div")(({ theme, $focused, $error }) => ({
  display: "flex",
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  background: theme.palette.background.primary,
  border: `1px solid ${
    $error
      ? theme.palette.text.warning_primary
      : $focused
        ? "rgba(150,46,255,0.6)"
        : "rgba(255,255,255,0.10)"
  }`,
  boxShadow: $focused ? "0 0 0 3px rgba(150,46,255,0.14)" : "none",
  transition: "border-color 150ms, box-shadow 150ms",
  boxSizing: "border-box",
}));

export const DarkTextareaInput = styled("textarea")(({ theme }) => ({
  flex: 1,
  background: "none",
  border: "none",
  fontSize: 14,
  outline: "none",
  fontFamily: "inherit",
  color: theme.palette.text.primary,
  padding: 0,
  resize: "vertical",
  minHeight: 100,
  lineHeight: 1.55,
  "&::placeholder": {
    color: "rgba(255,255,255,0.35)",
  },
}));
